import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SearchForm } from '../../../../shared/interfaces/search-filter';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { FilterComponent } from '../../../../shared/components/filter/filter.component';

@Component({
  selector: 'app-search-wncsbi',
  standalone: true,
  imports: [FormsModule, FilterComponent],
  templateUrl: './search-wncsbi.component.html',
  styleUrl: './search-wncsbi.component.scss',
})
export class SearchWncsbiComponent implements OnInit, OnChanges {
  @Input() showQuickFilters = true;
  @Input() showOutreachQuickFilter = true;
  @Input() showLoanQuickFilter = true;
  @Input() showAdvancedToggle = true;
  @Input() outreachFilterBy = '';
  @Input() loanFilterBy = '';
  @Input() outreachFilterOptions: { option: string; value: string }[] = [];
  @Input() loanFilterOptions: { option: string; value: string }[] = [];
  @Input() advancedSearchVisible?: boolean;

  @Output() clearSearch = new EventEmitter<SearchForm>();
  @Output() searchSubmitted = new EventEmitter<SearchForm>();
  @Output() reloadAllData = new EventEmitter<SearchForm>();
  @Output() outreachFilterChanged = new EventEmitter<string>();
  @Output() loanFilterChanged = new EventEmitter<string>();
  @Output() advancedToggleChanged = new EventEmitter<boolean>();
  @Output() filterCountChanged = new EventEmitter<number>();

  private auditLogService = inject(AuditLogService);

  isAdvancedSearchVisible = false;
  searchForm: SearchForm = {};
  isFormValid = false;
  showIndicator = false;
  count = 0;

  ngOnInit() {
    this.searchForm.grantSizeOperator = '';
    this.searchForm.requestedFundAmountOperator = '';
    this.searchForm.estimatedDamageValueOperator = '';
    this.searchForm.estimatedRevenueLossOperator = '';
    this.searchForm.disbursedAmountOperator = '';
    this.searchForm.grossRevenue2023Operator = '';
    this.searchForm.application_status = '';
    this.searchForm.ethnicity = '';
    this.searchForm.gender = '';
    this.searchForm.disability_status = '';
    this.searchForm.preferred_language = '';
    this.searchForm.sexual_orientation = '';
    this.searchForm.woman_led = '';
    this.searchForm.race = '';
    this.searchForm.address = '';
    this.searchForm.operating_status = '';
    this.checkFormValidity();
  }

  ngOnChanges() {
    if (typeof this.advancedSearchVisible === 'boolean') {
      this.isAdvancedSearchVisible = this.advancedSearchVisible;
    }
  }

  onSubmit() {
    if (!this.isFormValid) return;

    const formattedData = this.formatData(this.searchForm);
    this.count = this.countNonEmptyValues(formattedData);

    this.auditLogService.logFeature({
      featureName: 'wncsbi_applications',
      action: 'search',
      meta: {
        searchFiltersCount: String(this.count),
      },
      description: 'User searches WNCSBI applications',
    });

    this.showIndicator = this.count > 0;
    this.isAdvancedSearchVisible = false;
    this.advancedToggleChanged.emit(false);
    this.filterCountChanged.emit(this.count);
    this.searchSubmitted.emit(formattedData);
  }

  toggleAdvancedSearch() {
    this.isAdvancedSearchVisible = !this.isAdvancedSearchVisible;
    this.checkFormValidity();
    this.advancedToggleChanged.emit(this.isAdvancedSearchVisible);
  }

  hasErrorMessages(): boolean {
    return (
      ((parseFloat(this.searchForm.requestedFundAmount as string) ||
        parseFloat(this.searchForm.requestedFundAmount as string) === 0) &&
        !this.searchForm.requestedFundAmountOperator) ||
      (!this.searchForm.requestedFundAmount &&
        !!this.searchForm.requestedFundAmountOperator) ||
      ((parseFloat(this.searchForm.grossRevenue2023 as string) ||
        parseFloat(this.searchForm.grossRevenue2023 as string) === 0) &&
        !this.searchForm.grossRevenue2023Operator) ||
      (!this.searchForm.grossRevenue2023 &&
        !!this.searchForm.grossRevenue2023Operator) ||
      ((parseFloat(this.searchForm.grantSize as string) ||
        parseFloat(this.searchForm.grantSize as string) === 0) &&
        !this.searchForm.grantSizeOperator) ||
      (!this.searchForm.grantSize && !!this.searchForm.grantSizeOperator) ||
      ((parseFloat(this.searchForm.estimatedDamageValue as string) ||
        parseFloat(this.searchForm.estimatedDamageValue as string) === 0) &&
        !this.searchForm.estimatedDamageValueOperator) ||
      (!this.searchForm.estimatedDamageValue &&
        !!this.searchForm.estimatedDamageValueOperator) ||
      ((parseFloat(this.searchForm.estimatedRevenueLoss as string) ||
        parseFloat(this.searchForm.estimatedRevenueLoss as string) === 0) &&
        !this.searchForm.estimatedRevenueLossOperator) ||
      (!this.searchForm.estimatedRevenueLoss &&
        !!this.searchForm.estimatedRevenueLossOperator) ||
      ((parseFloat(this.searchForm.disbursedAmount as string) ||
        parseFloat(this.searchForm.disbursedAmount as string) === 0) &&
        !this.searchForm.disbursedAmountOperator) ||
      (!this.searchForm.disbursedAmount &&
        !!this.searchForm.disbursedAmountOperator)
    );
  }

  checkFormValidity() {
    const zipCode =
      typeof this.searchForm.postal_code === 'string'
        ? this.searchForm.postal_code?.trim() || ''
        : '';
    const isZipCodeValid = zipCode.length === 0 || zipCode.length > 4;

    const isAnyOtherFieldFilled = Object.entries(this.searchForm).some(
      ([key, value]) =>
        key !== 'postal_code' &&
        value !== undefined &&
        value !== null &&
        value.toString().trim().length > 0,
    );

    if (zipCode.length > 0 && !isZipCodeValid) {
      this.isFormValid = false;
      return;
    }

    this.isFormValid =
      isZipCodeValid && (zipCode.length > 4 || isAnyOtherFieldFilled);

    const formattedData = this.formatData(this.searchForm);
    this.count = this.countNonEmptyValues(formattedData);
    this.showIndicator = this.count > 0;
    this.filterCountChanged.emit(this.count);
  }

  isAnyOtherFieldFilled(): boolean {
    return Object.entries(this.searchForm).some(
      ([key, value]) =>
        key !== null &&
        value !== undefined &&
        value !== null &&
        value.toString().trim().length > 0,
    );
  }

  isShowAllData() {
    this.showIndicator = false;
    this.isAdvancedSearchVisible = false;
    this.advancedToggleChanged.emit(false);
    this.filterCountChanged.emit(0);
    this.reloadAllData.emit();
    this.searchForm = {
      grantSizeOperator: '',
      requestedFundAmountOperator: '',
      estimatedDamageValueOperator: '',
      estimatedRevenueLossOperator: '',
      disbursedAmountOperator: '',
      grossRevenue2023Operator: '',
      application_status: '',
      ethnicity: '',
      gender: '',
      disability_status: '',
      preferred_language: '',
      sexual_orientation: '',
      woman_led: '',
      race: '',
      operating_status: '',
    };
    this.checkFormValidity();
  }

  private formatData(form: SearchForm) {
    const formatRange = (
      value: string | undefined,
      operator: string | undefined,
    ) => {
      if (!value) return {};
      const parsedValue = parseInt(value, 10);
      return operator === 'greater'
        ? { min: parsedValue }
        : operator === 'less'
          ? { max: parsedValue }
          : {};
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formattedData: any = {
      address: form.address?.trim(),
      email: form.primary_email?.trim(),
      phone_number: form.phone_number?.trim(),
      postal_code:
        typeof form.postal_code === 'string' ? form.postal_code.trim() : '',
      owner_name: form.owner_name?.trim(),
      operating_status: form.operating_status?.trim(),
      race: form.race?.trim(),
      ethnicity: form.ethnicity?.trim(),
      gender: form.gender?.trim(),
      business_industry: form.business_industry?.trim(),
      sexual_orientation: form.sexual_orientation?.trim(),
      preferred_language: form.preferred_language?.trim(),
      application_status: form.application_status?.trim(),
      woman_led: form.woman_led?.trim(),
      owner_disabled: form.disability_status?.trim(),
      package_id: form.package_id?.trim(),
      funding_requested: formatRange(
        form.requestedFundAmount,
        form.requestedFundAmountOperator,
      ),
      estimated_damage_value: formatRange(
        form.estimatedDamageValue,
        form.estimatedDamageValueOperator,
      ),
      estimated_revenue_loss: formatRange(
        form.estimatedRevenueLoss,
        form.estimatedRevenueLossOperator,
      ),
      grant_size: formatRange(form.grantSize, form.grantSizeOperator),
      disbursed_amount: formatRange(
        form.disbursedAmount,
        form.disbursedAmountOperator,
      ),
      gross_revenue_2023: formatRange(
        form.grossRevenue2023,
        form.grossRevenue2023Operator,
      ),
    };

    // Remove empty, null, or undefined fields
    Object.keys(formattedData).forEach((key) => {
      if (
        formattedData[key] === '' ||
        (typeof formattedData[key] === 'object' &&
          Object.keys(formattedData[key]).length === 0)
      ) {
        delete formattedData[key];
      }
    });

    return formattedData;
  }

  private countNonEmptyValues(data: Record<string, unknown>): number {
    return Object.values(data).filter(
      (value) => value !== undefined && value !== null && value !== '',
    ).length;
  }
}
